/**
 * ConversationsPage — the operator's global Conversations workspace
 * (DIGEMP-GLOBAL-CONVERSATIONS-FRONTEND-072).
 *
 * One inbox over every Digital Employee conversation — direct threads, manager-routed threads and
 * ticket-linked threads — reading the read-only conversation-center projection
 * (DIGEMP-GLOBAL-CONVERSATIONS-BACKEND-072: GET /api/agents/conversation-center and
 * /{conversation_id}). The server owns thread classification, attention (derived from the LIVE
 * AgentRun, never from a possibly-stale trace), resume context and activity counters; this page
 * renders them in Hebrew and adds only what a browser can know: search, local filters and the
 * browser-local read cursor. Chat, approvals and retries stay in ConversationPanel via
 * /conversations/{id} — this page never re-implements them.
 *
 * Truthfulness rules this page follows:
 *   - attention, human_status and every label are the server's decision; they are shown as given,
 *     including "לא ידוע" — an unverifiable thread is never rounded up to progress or completion.
 *   - "new for you" is a browser-local read cursor over message sequence_no
 *     (components/conversationReadState). A thread this browser never opened is never claimed as
 *     new, and because the list does not read transcripts the page never shows a count it cannot
 *     verify. The server's own counter (messages since the operator's last message) is shown
 *     separately and labeled as exactly that.
 *   - no raw payloads, logs, tool output or model text — only allow-listed presentation fields.
 *   - missing evidence stays visible: a vanished ticket reference, a capped scan and stale
 *     metadata each get an explicit line instead of being silently dropped.
 */
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { conversationCenterApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { loadReadState, markConversationsRead } from "../components/conversationReadState";
import {
  attentionBadgeClass,
  formatRelative,
  formatFull,
  threadTicketCustomer,
} from "../components/conversationPresentation";
import { conversationStatusBadgeClass } from "../components/conversationStatusVocabulary";

const LIST_LIMIT = 50;   // threads on this page (server order: newest first)
const POLL_MS = 30000;

/** Browser-local: only a thread this browser has already seen can be "new" — never a guess. */
function isNewFor(thread, readState) {
  const cursor = readState?.[thread?.id];
  const latest = thread?.activity?.latest_sequence_no;
  return typeof cursor === "number" && typeof latest === "number" && latest > cursor;
}

function threadMatchesSearch(thread, query) {
  if (!query) return true;
  const resume = thread.resume || {};
  const haystack = [
    thread.id,
    thread.thread_type_label,
    thread.status_label,
    thread.human_status,
    thread.company_name || "",
    thread.ticket ? `${thread.ticket.ticket_number ?? ""} ${thread.ticket.title}` : "",
    // The haystack is what the row draws, so the ticket's own customer belongs in it for the same
    // reason its title does.
    threadTicketCustomer(thread) || "",
    ...(thread.employees || []).map((e) => `${e.name} ${e.role_title || ""}`),
    ...(thread.operators || []).map((o) => o.display_name || ""),
    resume.last_employee_turn ? `${resume.last_employee_turn.author_name} ${resume.last_employee_turn.excerpt}` : "",
    resume.last_operator_turn?.excerpt || "",
  ].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase().trim());
}

function TicketChip({ thread }) {
  if (thread.ticket) {
    // The `לקוח` chip on this row states the thread's customer; the ticket's own customer is a
    // second served fact and is drawn here, on the ticket's own chip, only when it differs.
    const ticketCustomer = threadTicketCustomer(thread);
    return (
      <Link
        to={`/tickets/${thread.ticket.id}?tab=ai-chat`}
        className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:underline"
        title={thread.ticket.title}
      >
        קריאה {thread.ticket.ticket_number ? `#${thread.ticket.ticket_number}` : String(thread.ticket.id).slice(0, 8)}
        {thread.ticket.title ? ` · ${thread.ticket.title}` : ""}
        {ticketCustomer ? <span data-testid="ticket-chip-customer"> · {ticketCustomer}</span> : null}
      </Link>
    );
  }
  if (thread.thread_type === "ticket") {
    // The conversation is ticket-linked but its ticket row is gone — say so, never invent a link.
    return (
      <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" title="פרטי הקריאה המקושרת לא נטענו">
        מקושרת לקריאה — פרטי הקריאה לא נטענו
      </span>
    );
  }
  return null;
}

function EmployeeStack({ employees }) {
  if (employees.length === 0) {
    return <span className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-lg">🧭</span>;
  }
  return (
    <span className="flex -space-x-2 space-x-reverse shrink-0">
      {employees.slice(0, 3).map((e) => (
        <EmployeeAvatar
          key={e.agent_id || e.name}
          name={e.name}
          avatarKey={e.avatar_key}
          avatarColor={e.avatar_color}
          status={e.availability_status}
          isEnabled={e.is_enabled}
          showStatus={Boolean(e.availability_status)}
          size="md"
        />
      ))}
    </span>
  );
}

function ThreadCard({ thread, isNew, onOpen, onMarkRead }) {
  const activeEmployees = thread.employees.filter((e) => e.is_active);
  const shownEmployees = activeEmployees.length > 0 ? activeEmployees : thread.employees;
  const primary = shownEmployees[0] || null;
  const extra = shownEmployees.length - 1;
  const resume = thread.resume || {};
  const stage = resume.current_stage;
  const operatorNames = thread.operators.map((o) => o.display_name).filter(Boolean);

  return (
    <article
      className="bg-white rounded-xl border shadow-sm p-3.5 space-y-2"
      aria-label={`שיחה עם ${shownEmployees.map((e) => e.name).join(", ") || "ללא עובד משויך"}`}
    >
      <div className="flex items-start gap-3">
        <EmployeeStack employees={shownEmployees} />

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-semibold text-gray-800 text-sm">
              {shownEmployees.length === 0
                ? "ללא עובד משויך"
                : `${primary.name}${extra > 0 ? ` ועוד ${extra}` : ""}`}
            </span>
            {primary?.role_title && <span className="text-xs text-gray-500">{primary.role_title}</span>}
            {activeEmployees.length === 0 && shownEmployees.length > 0 && (
              <span className="text-xs text-gray-400">(אין עובד פעיל בשיחה)</span>
            )}
            <span className="text-xs text-gray-400 mr-auto" title={formatFull(thread.last_activity_at || thread.updated_at)}>
              פעילות אחרונה: {formatRelative(thread.last_activity_at || thread.updated_at) || "לא ידוע"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{thread.thread_type_label}</span>
            <TicketChip thread={thread} />
            {thread.company_name && (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">לקוח: {thread.company_name}</span>
            )}
            {thread.has_manager && thread.thread_type !== "manager" && (
              <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">🧭 מנהל הצוות בשיחה</span>
            )}
            {operatorNames.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {operatorNames.length === 1 ? `נציג: ${operatorNames[0]}` : `נציגים: ${operatorNames.join(", ")}`}
              </span>
            )}
            {isNew && (
              <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white font-medium">חדש עבורך</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[11px] ${attentionBadgeClass(thread.attention)}`}>
              {thread.attention_label}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] ${conversationStatusBadgeClass(thread.status)}`}>
              {thread.status_label}
            </span>
            {stage?.stage_label && (
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-violet-50 text-violet-700">
                {stage.stage_label}
                {stage.since ? ` · מאז ${formatRelative(stage.since)}` : ""}
              </span>
            )}
            {resume.updates_pending && (
              <span
                className="px-2 py-0.5 rounded-full text-[11px] bg-amber-50 text-amber-800"
                title="מצב המשימה התקדם מאז הדיווח האחרון שנכתב בשיחה"
              >
                התקדמות חדשה מאז העדכון בשיחה
              </span>
            )}
          </div>

          <p className="text-sm text-gray-700">{thread.human_status}</p>

          {(resume.last_employee_turn || resume.last_operator_turn) && (
            <div className="border-t border-gray-100 pt-1.5 space-y-0.5">
              {resume.last_employee_turn && (
                <p className="text-xs text-gray-700 truncate">
                  <span className="font-medium">{resume.last_employee_turn.author_name}: </span>
                  {resume.last_employee_turn.excerpt}
                  <span className="text-gray-400"> · {formatRelative(resume.last_employee_turn.at)}</span>
                </p>
              )}
              {resume.last_operator_turn && (
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-medium">הטכנאי: </span>
                  {resume.last_operator_turn.excerpt}
                  <span className="text-gray-400"> · {formatRelative(resume.last_operator_turn.at)}</span>
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link
              to={`/conversations/${thread.id}`}
              onClick={onOpen}
              className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              המשך בשיחה
            </Link>
            {stage?.run_id && (
              <a href={`/agent-runs/${stage.run_id}`} className="px-3 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">
                לפרטי המשימה
              </a>
            )}
            {isNew && (
              <button
                type="button"
                onClick={onMarkRead}
                className="px-3 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                סמן כנקרא
              </button>
            )}
            <span className="text-gray-400">{thread.message_count} הודעות</span>
            {thread.activity.messages_since_operator_turn > 0 && (
              <span className="text-gray-400" title="הודעות העובד או המערכת מאז ההודעה האחרונה שלך">
                {thread.activity.messages_since_operator_turn} מאז ההודעה האחרונה שלך
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ConversationsPage() {
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [attentionFilter, setAttentionFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [readState, setReadState] = useState(loadReadState);

  const { data, isLoading, isError, isSuccess, refetch, isFetching } = useQuery({
    queryKey: ["conversation-center"],
    queryFn: () => conversationCenterApi.list({ limit: LIST_LIMIT }),
    enabled: canView,
    refetchInterval: POLL_MS,
  });

  // Never crash the whole desk on a malformed/partial API response. Keep the issue
  // visible rather than silently treating missing data as an empty conversation list.
  const threads = useMemo(() => Array.isArray(data?.threads) ? data.threads : [], [data]);
  const payloadInvalid = isSuccess && !Array.isArray(data?.threads);
  const safeThreads = useMemo(() => threads.filter((thread) =>
    thread && typeof thread.id === "string"
    && Array.isArray(thread.employees)
    && thread.employees.every((employee) => employee && typeof employee.name === "string")
    && Array.isArray(thread.operators)
    && thread.operators.every((operator) => operator && typeof operator === "object")
    && thread.activity && typeof thread.activity === "object"
    && thread.resume && typeof thread.resume === "object"
  ), [threads]);
  const invalidThreadCount = threads.length - safeThreads.length;
  const summary = data?.summary || null;
  const scan = data?.scan || null;

  const decorated = useMemo(
    () => safeThreads.map((thread) => ({ thread, isNew: isNewFor(thread, readState) })),
    [safeThreads, readState],
  );

  const stats = useMemo(() => {
    let isNew = 0;
    for (const item of decorated) if (item.isNew) isNew += 1;
    return { isNew };
  }, [decorated]);

  const statusesPresent = useMemo(
    () => [...new Set(safeThreads.map((t) => t.status))].sort(),
    [safeThreads],
  );

  const statusLabels = useMemo(() => {
    const map = new Map();
    for (const t of safeThreads) map.set(t.status, t.status_label);
    return map;
  }, [safeThreads]);

  const visible = useMemo(() => decorated.filter(({ thread, isNew }) => {
    if (typeFilter && thread.thread_type !== typeFilter) return false;
    if (statusFilter && thread.status !== statusFilter) return false;
    if (attentionFilter !== "all" && thread.attention !== attentionFilter) return false;
    if (unreadOnly && !isNew) return false;
    return threadMatchesSearch(thread, search);
  }), [decorated, typeFilter, statusFilter, attentionFilter, unreadOnly, search]);

  // The link itself navigates; this only records the read cursor before leaving the page.
  const handleOpen = useCallback((thread) => {
    setReadState(markConversationsRead([{ id: thread.id, lastSeenSeq: thread.activity.latest_sequence_no }]));
  }, []);

  const markRead = useCallback((thread) => {
    setReadState(markConversationsRead([{ id: thread.id, lastSeenSeq: thread.activity.latest_sequence_no }]));
  }, []);

  const markAllVisibleRead = useCallback(() => {
    const entries = visible
      .filter(({ isNew }) => isNew)
      .map(({ thread }) => ({ id: thread.id, lastSeenSeq: thread.activity.latest_sequence_no }));
    if (entries.length > 0) setReadState(markConversationsRead(entries));
  }, [visible]);

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בשיחות AI</div>;
  }

  const filtersActive = Boolean(search || typeFilter || statusFilter || unreadOnly || attentionFilter !== "all");
  const clearFilters = () => {
    setSearch("");
    setTypeFilter("");
    setStatusFilter("");
    setAttentionFilter("all");
    setUnreadOnly(false);
  };

  const tiles = [
    { key: "all", label: "הכל", count: summary?.total ?? threads.length },
    { key: "needs_you", label: "דורשות טיפול", count: summary?.needs_you ?? 0 },
    { key: "working", label: "בעבודה", count: summary?.working ?? 0 },
    { key: "waiting_customer", label: "ממתין ללקוח", count: summary?.waiting_customer ?? 0 },
    { key: "settled", label: "הסתיימו", count: summary?.settled ?? 0 },
    { key: "neutral", label: "רגילות", count: summary?.neutral ?? 0 },
    { key: "unknown", label: "לא ידוע", count: summary?.unknown ?? 0 },
  ];

  return (
    <div className="w-full max-w-[1100px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">מרכז השיחות</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            כל השיחות עם העובדים הדיגיטליים — ישירות, דרך המנהל ומתוך קריאות.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-sm px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {isFetching ? "מרענן…" : "רענן"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
        {tiles.map((tile) => (
          <button
            key={tile.key}
            type="button"
            onClick={() => { setAttentionFilter(tile.key); setUnreadOnly(false); }}
            aria-pressed={attentionFilter === tile.key && !unreadOnly}
            className={`text-right rounded-xl border px-3 py-2 ${
              attentionFilter === tile.key && !unreadOnly ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <div className={`text-lg font-semibold ${tile.key === "needs_you" && tile.count > 0 ? "text-rose-600" : "text-gray-800"}`}>
              {tile.count}
            </div>
            <div className="text-xs text-gray-500">{tile.label}</div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setUnreadOnly((v) => !v)}
          aria-pressed={unreadOnly}
          className={`text-right rounded-xl border px-3 py-2 ${
            unreadOnly ? "border-rose-400 bg-rose-50" : "border-gray-200 bg-white hover:bg-gray-50"
          }`}
        >
          <div className="text-lg font-semibold text-gray-800">{stats.isNew}</div>
          <div className="text-xs text-gray-500">חדשות לך</div>
        </button>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש לפי עובד, נציג, קריאה, לקוח או תוכן הודעה…"
            aria-label="חיפוש שיחות"
            className="flex-1 min-w-[220px] text-sm border border-gray-200 rounded px-3 py-1.5"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="סוג שיחה"
            className="text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            <option value="">כל הסוגים</option>
            <option value="direct">שיחה ישירה</option>
            <option value="manager">ניתוב דרך המנהל</option>
            <option value="ticket">מקושרת לקריאה</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="סטטוס שיחה"
            className="text-sm border border-gray-200 rounded px-2 py-1.5"
          >
            <option value="">כל הסטטוסים</option>
            {statusesPresent.map((s) => (
              <option key={s} value={s}>{statusLabels.get(s) || s}</option>
            ))}
          </select>
          {stats.isNew > 0 && (
            <button
              type="button"
              onClick={markAllVisibleRead}
              className="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              סמן הכל כנקרא
            </button>
          )}
        </div>

        <p className="text-[11px] text-gray-400">
          {scan && scan.considered > threads.length
            ? `מוצגות ${threads.length} מתוך ${scan.considered} השיחות שנסרקו. `
            : ""}
          {scan?.capped ? "נסרקו רק 200 השיחות האחרונות — ייתכן שיש שיחות ותיקות יותר שאינן נכללות. " : ""}
          {data?.generated_at ? `נכון ל-${formatFull(data.generated_at)}. ` : ""}
          סימון "חדש עבורך" נשמר בדפדפן זה בלבד ואינו מדווח לשרת.
        </p>
      </div>

      {isLoading && <p className="text-gray-400 text-sm">טוען...</p>}
      {!isLoading && !isError && (payloadInvalid || invalidThreadCount > 0) && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          נתוני מרכז השיחות אינם תקינים. {invalidThreadCount > 0
            ? invalidThreadCount === 1
              ? "שיחה אחת לא הוצגה בגלל נתונים חסרים."
              : `${invalidThreadCount} שיחות לא הוצגו בגלל נתונים חסרים.`
            : "לא התקבלה רשימת שיחות תקינה."}
          <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}
      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          שגיאה בטעינת השיחות
          <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}
      {!isLoading && !isError && !payloadInvalid && threads.length === 0 && (
        <p className="text-gray-400 text-sm italic">אין שיחות עדיין</p>
      )}
      {!isLoading && !isError && safeThreads.length > 0 && visible.length === 0 && (
        <div className="text-sm text-gray-500 bg-white border rounded-xl px-3 py-2">
          אין שיחות שמתאימות לסינון
          {filtersActive && (
            <button type="button" onClick={clearFilters} className="mr-2 text-blue-600 underline">נקה סינון</button>
          )}
        </div>
      )}

      <div className="space-y-2.5">
        {visible.map(({ thread, isNew }) => (
          <ThreadCard
            key={thread.id}
            thread={thread}
            isNew={isNew}
            onOpen={() => handleOpen(thread)}
            onMarkRead={() => markRead(thread)}
          />
        ))}
      </div>
    </div>
  );
}
